import * as React from "react";
import {observer} from "mobx-react";
import {LayerSelection, LayerFilter} from "../../app-mobx";
import {autorun} from "mobx";
import {SymbolConfiguration} from "../../../../config/map-viewer-configuration";
import {PathUtils} from "../../../../utils/path-utils";
import ViewClickEvent = __esri.ViewClickEvent;
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {IAppStore, withStore} from "../../../../core/stores/abstract-app-store";

export interface FeatureSelectionProps {
    symbols: SymbolConfiguration;
    store?: IAppStore; // this is injected by withStore
}

@observer
class FeatureSelectionBase extends React.Component<FeatureSelectionProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    layerName: string;
    constructor(props: FeatureSelectionProps) {
        super(props);
        this.layerName = "highlight";
    }

    /**
     * Waiting for the map being ready before selecting features on layers.
     */
    componentDidMount(): void {
        const { store} = this.props;
        this.autorunDisposers.push(autorun(() => {
            if(!store){
                return;
            }
            const {backgroundLayers, mode, visibleLayers, usedLayers} = store.appStore;
            const {map, view} = store.mapStore;
            if (!map || ! view) {
                return;
            }
            map.layers.flatten((layer: __esri.Layer) =>
                (layer as __esri.MapImageLayer).allSublayers
            ).forEach((layer: __esri.FeatureLayer | __esri.Sublayer) => {
                layer.visible = (usedLayers.includes(layer.title)
                    || visibleLayers.some(
                        (x) => {
                            return this.layerInArray(x, layer);
                        }
                    )
                    || backgroundLayers.some(
                        (x) => {
                            return this.layerInArray(x, layer);
                        }
                    )
                );
            });
            if (mode === "raster") {
                if(this.watchHandles.length === 0) {
                    this.watchHandles.push(view.on("click", (evt: ViewClickEvent) => {
                        map.layers.flatten((layer: __esri.Layer) =>
                            (layer as any).allSublayers
                        ).forEach((layer: __esri.FeatureLayer | __esri.MapImageLayer) => {
                            if ((layer.type === "feature"
                                    || (layer as __esri.Sublayer).layer?.type === "map-image")
                                && layer.visible && !backgroundLayers.some(
                                (x) => { return this.layerInArray(x, layer); }
                            )) {
                                const query = (layer as FeatureLayer).createQuery();
                                query.outFields = ["*"];
                                query.geometry = evt.mapPoint;
                                (layer as FeatureLayer).queryFeatures(query).then((result: __esri.FeatureSet) => {
                                    result.features.forEach((graphic: __esri.Graphic) => {
                                        store?.appStore.addGraphic(this.layerName, [graphic], true);
                                    });
                                });
                            }
                        });
                    }));
                }
                this.applyFilters(map, store.appStore.mapFilters, store);
            } else {
                const layer = view.map.layers.find(
                    (l: __esri.GraphicsLayer) => l.title === this.layerName) as __esri.GraphicsLayer;
                if (layer) {
                    view.map.remove(layer);
                }
                const textLayer = view.map.layers.find(
                    (l: __esri.GraphicsLayer) => l.title === this.layerName + "_text") as __esri.GraphicsLayer;
                if (textLayer) {
                    view.map.remove(textLayer);
                }
            }
        }));
        this.autorunDisposers.push(autorun(() => {
            if(!store){
                return;
            }
            const {mapFilters, visibleLayers} = store.appStore;
            const {map, view} = store.mapStore;
            if (!map || ! view || visibleLayers?.length === 0) {
                return;
            }
            this.applyFilters(map, mapFilters, store);
        }));
    }
    applyFilters(map: __esri.Map, mapFilters: LayerFilter[], store: IAppStore){
        map.layers.flatten((layer: __esri.Layer) =>
            (layer as __esri.MapImageLayer).allSublayers
        ).forEach((layer: __esri.FeatureLayer | __esri.Sublayer) => {
            if(layer.hasOwnProperty("definitionExpression")){
                (layer as __esri.Sublayer).definitionExpression = "1=1";
            }
        });
        mapFilters.forEach((filter: LayerFilter) => {
            const targetLayers = store?.mapStore.getLayersByMetauid(filter.metauid, [filter.layerId]);
            if(targetLayers?.length===1) {
                (targetLayers[0] as __esri.MapImageLayer).findSublayerById(
                    filter.layerId).definitionExpression = filter.query;
            }
        });
    }
    layerInArray(selection: string | LayerSelection,
                 layer: __esri.FeatureLayer | __esri.MapImageLayer | __esri.Sublayer): boolean {
        let url = (layer as any)?.parsedUrl?.path;
        if (!url) {
            url = layer.url; // MapImageLayer Service
        }
        const uid: string | null =
            (typeof url === "string")? PathUtils.getMetaId(url).toLowerCase(): null;
        const layerId: number | null =
            (typeof url === "string")? PathUtils.getLayerId(url): (layer as any).layerId;
        return (typeof selection === "string" && (uid === selection.toLowerCase()
            || layer.title.toLowerCase().includes(selection.toLowerCase())))
            || (typeof selection === "object" && uid === selection.metauid.toLowerCase() &&
                ((typeof selection.layerId !== "undefined" && selection.layerId === layerId)
                    || (Array.isArray(selection.layerIds) && typeof layerId === "number"
                        && selection.layerIds.includes(layerId))
                    || (layer as any).allSublayers));
    }
    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }

    render(): JSX.Element | null {
        return null;
    }
}
export const FeatureSelection = withStore(FeatureSelectionBase);
